import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StandardPageLayout } from '@/components/ui/standard-page-layout';
import {
  overviewAPI,
  approvalsAPI,
  kpiAPI,
  settingsAPI,
  killSwitchAPI,
  connectionsAPI,
  type KPIUpsertPayload,
  type SettingsResponse,
} from '@/services/api';
import type { ApprovalsListResponse, ApprovalRecord } from '@/services/api';
import type { KPI, KPIHistoryPoint } from '@shared/schema';
import { Button } from '@/components/ui/button';
import {
  TrendingUp,
  Bot,
  Clock,
  DollarSign,
  Activity,
  AlertTriangle,
  Bell,
  FileCheck,
  AlertOctagon,
  SlidersHorizontal,
  Plus,
  Check,
  X,
  CheckCircle2,
  ChevronDown,
  StopCircle,
  Loader2,
} from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { EnhancedChart } from '@/components/ui/enhanced-chart';
import { TimeRangeSelector, type TimeRange, type TimeRangeData } from '@/components/ui/time-range-selector';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useScope } from '@/contexts/ScopeContext';
import { cn } from '@/lib/utils';
import { useRBAC } from '@/lib/rbac';
import { useToast } from '@/hooks/use-toast';
import { KPIManagerDialog } from '@/components/kpi/KPIManagerDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { LiveIndicator } from '@/components/ui/live-indicator';
import { ExchangeSummary } from '@/components/ui/exchange-summary';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import {
  ensureLocale,
  formatCurrency,
  formatNumber,
  formatPercent,
  parseNumeric,
  thinSpace,
} from '@/lib/number-format';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sparkline, SparklineTrend } from '@/components/ui/sparkline';
import { AlertNotificationBar, type AlertNotificationItem } from '@/components/ui/alert-notification-bar';
import { QuickStatsCard, type QuickStat } from '@/components/ui/quick-stats-card';
import { TabbedChart, type TabbedChartTab } from '@/components/ui/tabbed-chart';
import { LiveFeed, type LiveFeedItem, type LiveFeedSummary, type FeedItemType } from '@/components/ui/live-feed';
import {
  EnhancedExchangeSummary,
  type EnhancedExchangeConnection,
  type EnhancedWalletConnection,
} from '@/components/ui/enhanced-exchange-summary';

const MAX_KPI_SELECTION = 6;
const KILL_SWITCH_CONFIRMATION = 'PAUSE ALL BOTS';
const OVERVIEW_FALLBACK_POLL_MS = 10_000;
const TRADE_PNL_DISPLAY_EPSILON = 0.005;
const TRADE_QUANTITY_ZERO_EPSILON = 1e-12;
const PORTFOLIO_NAV_KPI_IDS = ['portfolio_nav_chf', 'portfolio_equity', 'portfoliovalue', 'kpi-portfolio'];
const PORTFOLIO_NAV_KPI_LABELS = ['total portfolio value', 'portfolio value'];
const REALIZED_PNL_KPI_IDS = ['realized_pnl_chf', 'daily_pnl', 'kpi-pnl'];
const REALIZED_PNL_KPI_LABELS = ['realized p&l', 'realized pnl', 'daily p&l', 'daily pnl'];

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const APPROVAL_TYPE_LABELS: Record<string, string> = {
  mode_change: 'Mode Change',
  risk_limit: 'Risk Limit',
  venue_activation: 'Venue Activation',
};

type PlaceholderCardProps = {
  title: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
};

const PlaceholderCard = ({ title, description, icon, className }: PlaceholderCardProps) => (
  <Card className={cn('border-2 border-dashed border-slate-200 bg-white/80 shadow-none', className)}>
    <CardHeader className="space-y-2">
      <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-800 sm:text-lg">
        {icon ? <span aria-hidden="true" className="text-inherit">{icon}</span> : null}
        {title}
      </CardTitle>
    </CardHeader>
    {description && (
      <CardContent className="text-sm text-slate-500 leading-relaxed">{description}</CardContent>
    )}
  </Card>
);

type FootnoteTone = 'positive' | 'negative' | 'neutral';

interface OverviewKpiFootnote {
  label: string;
  value: string;
  tone?: FootnoteTone;
  tooltip?: string;
}

interface OverviewKpiChip {
  label: string;
  tone: FootnoteTone;
}

interface OverviewKpiCardProps {
  title: string;
  value: string;
  valueTone?: FootnoteTone;
  valueTooltip?: string;
  icon?: React.ReactNode;
  footnotes: OverviewKpiFootnote[];
  chip?: OverviewKpiChip;
  sparklineData?: number[];
  sparklineTrend?: 'up' | 'down' | 'neutral';
  href?: string;
}

const chipToneClasses: Record<FootnoteTone, string> = {
  positive: 'bg-emerald-100 text-emerald-700',
  negative: 'bg-rose-100 text-rose-700',
  neutral: 'bg-amber-100 text-amber-700',
};

const footnoteToneClasses: Record<FootnoteTone, string> = {
  positive: 'text-emerald-600',
  negative: 'text-rose-600',
  neutral: 'text-slate-500',
};

const valueToneClasses: Record<FootnoteTone, string> = {
  positive: 'text-emerald-600',
  negative: 'text-rose-600',
  neutral: 'text-slate-900',
};

const OverviewKpiCard = ({
  title,
  value,
  valueTone = 'neutral',
  valueTooltip,
  icon,
  footnotes,
  chip,
  sparklineData,
  sparklineTrend,
  href,
}: OverviewKpiCardProps) => {
  const [, navigate] = useLocation();

  const handleClick = useCallback(() => {
    if (href) {
      navigate(href);
    }
  }, [href, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (href && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        navigate(href);
      }
    },
    [href, navigate]
  );

  return (
    <div
      className={cn(
        'relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm focus-within:ring-2 focus-within:ring-brand-red/40 focus-within:ring-offset-2',
        href && 'cursor-pointer transition-all hover:border-slate-300 hover:shadow-md'
      )}
      tabIndex={0}
      role={href ? 'button' : 'group'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={href ? `${title}: ${value}. Click to view details.` : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-slate-500" data-testid="kpi-title">
              {title}
            </p>
            {sparklineTrend && <SparklineTrend trend={sparklineTrend} className="text-xs" />}
          </div>
          {sparklineData && sparklineData.length > 1 && (
            <div className="mt-1">
              <Sparkline
                data={sparklineData}
                width={80}
                height={20}
                trend={sparklineTrend}
                showArea
              />
            </div>
          )}
          <p
            className={cn('text-xl font-bold tabular-nums', valueToneClasses[valueTone])}
            title={valueTooltip}
            data-testid="kpi-value"
          >
            {value}
          </p>
        </div>
        {chip ? (
          <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap', chipToneClasses[chip.tone])}>
            {chip.label}
          </span>
        ) : icon ? (
          <div className="text-slate-400" data-testid="kpi-icon">
            {icon}
          </div>
        ) : null}
      </div>

      <div className="mt-3 space-y-1.5">
        {footnotes.map((note, index) => (
          <div
            key={`${note.label}-${index}`}
            className="flex items-center justify-between text-[11px] text-slate-500"
            title={note.tooltip}
          >
            <span className="font-medium text-slate-400">{note.label}</span>
            <span className={cn('tabular-nums font-semibold', footnoteToneClasses[note.tone ?? 'neutral'])}>
              {note.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const OverviewKpiSkeleton = () => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="animate-pulse space-y-2">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-6 w-24" />
      <Skeleton className="h-2.5 w-28" />
      <Skeleton className="h-2.5 w-24" />
    </div>
  </div>
);

const OverviewKpiGrid = ({
  children,
  className,
  ...rest
}: { children: React.ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6 auto-rows-fr', className)} {...rest}>
    {children}
  </div>
);

type LiveStatus = 'connected' | 'connecting' | 'reconnecting';

type TimeRangeState = {
  value: TimeRange;
  from?: string;
  to?: string;
};

const DEFAULT_TIME_RANGE: TimeRange = '7d';

const toneFromNumber = (value: number | null): FootnoteTone => {
  if (value === null) return 'neutral';
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
};

const sortHistoryAscending = (history?: KPIHistoryPoint[] | null): KPIHistoryPoint[] => {
  if (!history) return [];
  return [...history].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

const computeHistoryMetrics = (kpi: KPI) => {
  const history = sortHistoryAscending(kpi.history);
  const latestFromHistory = history.length > 0 ? parseNumeric(history.at(-1)?.value) : null;
  const previousFromHistory = history.length > 1 ? parseNumeric(history.at(-2)?.value) : null;
  const currentValue = parseNumeric(kpi.value) ?? latestFromHistory ?? null;
  const previousValue = previousFromHistory ?? (currentValue !== null && typeof kpi.change === 'number'
    ? currentValue / (1 + kpi.change / 100)
    : null);

  const deltaAbsolute = currentValue !== null && previousValue !== null ? currentValue - previousValue : null;
  const deltaPercent = currentValue !== null && previousValue
    ? (previousValue === 0 ? null : (deltaAbsolute ?? 0) / previousValue * 100)
    : typeof kpi.change === 'number'
      ? kpi.change
      : null;

  const lastSevenValues = history
    .map((point) => parseNumeric(point.value))
    .filter((value): value is number => value !== null)
    .slice(-7);

  const sevenDayAverage = lastSevenValues.length > 0
    ? lastSevenValues.reduce((total, value) => total + value, 0) / lastSevenValues.length
    : null;

  const deltaVsAverageAbsolute = currentValue !== null && sevenDayAverage !== null
    ? currentValue - sevenDayAverage
    : null;

  const deltaVsAveragePercent = currentValue !== null && sevenDayAverage
    ? (sevenDayAverage === 0 ? null : (deltaVsAverageAbsolute ?? 0) / sevenDayAverage * 100)
    : null;

  const sparklineValues = history
    .map((point) => parseNumeric(point.value))
    .filter((value): value is number => value !== null)
    .slice(-14);

  const sparklineTrend: 'up' | 'down' | 'neutral' =
    sparklineValues.length >= 2
      ? sparklineValues[sparklineValues.length - 1] > sparklineValues[0] * 1.001
        ? 'up'
        : sparklineValues[sparklineValues.length - 1] < sparklineValues[0] * 0.999
          ? 'down'
          : 'neutral'
      : 'neutral';

  return {
    currentValue,
    deltaAbsolute,
    deltaPercent,
    deltaVsAverageAbsolute,
    deltaVsAveragePercent,
    sparklineValues,
    sparklineTrend,
  };
};

const buildCurrencyFootnote = (
  label: string,
  deltaAbs: number | null,
  deltaPct: number | null,
  locale: string,
  options: { neutralTone?: boolean } = {},
): OverviewKpiFootnote => {
  if (deltaAbs === null && deltaPct === null) {
    return { label, value: '—', tone: 'neutral' };
  }

  const parts: string[] = [];
  if (deltaAbs !== null) {
    parts.push(formatCurrency(deltaAbs, { locale, decimals: 0, sign: 'always' }));
  }
  if (deltaPct !== null) {
    parts.push(`(${formatPercent(deltaPct, { decimals: 1, sign: 'always' })})`);
  }

  return {
    label,
    value: parts.join(' ').trim(),
    tone: options.neutralTone ? 'neutral' : toneFromNumber(deltaAbs ?? deltaPct ?? 0),
  };
};

const buildNumberFootnote = (
  label: string,
  value: number | null,
  digits = 0,
  locale?: string,
  options: { neutralTone?: boolean } = {},
): OverviewKpiFootnote => ({
  label,
  value: value === null ? '—' : formatNumber(value, { locale, decimals: digits, sign: 'always' }),
  tone: options.neutralTone ? 'neutral' : toneFromNumber(value),
});

const buildPercentFootnote = (
  label: string,
  value: number | null,
  decimals = 1,
  locale?: string,
  options: { neutralTone?: boolean } = {},
): OverviewKpiFootnote => ({
  label,
  value: value === null ? '—' : formatPercent(value, { locale, decimals, sign: 'always' }),
  tone: options.neutralTone ? 'neutral' : toneFromNumber(value),
});

const getSharpeChip = (value: number | null): OverviewKpiChip | undefined => {
  if (value === null) return undefined;
  const formatted = formatNumber(value, { decimals: 2, sign: 'never' });
  if (value >= 1.5) {
    return { label: `Good · ${formatted}`, tone: 'positive' };
  }
  if (value >= 1.0) {
    return { label: `Fair · ${formatted}`, tone: 'neutral' };
  }
  return { label: `Watch · ${formatted}`, tone: 'negative' };
};

const formatRelativeTime = (timestamp: string) => {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 1) return 'moments ago';
  if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
};

type OverviewKPIResponse = KPI;

type OverviewMetricPointResponse = {
  date: string;
  equity: number;
  pnl: number;
  volume: number;
  trades: number;
};

type OverviewAlertResponse = {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical' | string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
};

type OverviewTradeResponse = {
  id: string;
  tradeUuid?: string | null;
  symbol: string;
  side: string;
  quantity: number;
  price: number;
  pnl: number;
  executedAt: string;
  botId: string;
};

type OverviewBotsSummary = {
  total: number;
  running: number;
  paused: number;
  stopped: number;
  live: number;
  paper: number;
};

type OverviewResponse = {
  kpis?: OverviewKPIResponse[];
  metrics?: {
    portfolio?: OverviewMetricPointResponse[];
    tenant?: OverviewMetricPointResponse[];
    organization?: OverviewMetricPointResponse[];
  };
  alerts?: OverviewAlertResponse[];
  trades?: OverviewTradeResponse[];
  bots?: OverviewBotsSummary;
};

type ConnectionRecord = {
  id: string;
  connectionUuid?: string | null;
  legacyId?: number | string | null;
  type?: string | null;
  connectionType?: string | null;
  connection_type?: string | null;
  network?: string | null;
  label?: string | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  tags?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type ConnectionSummaryCard = {
  id: string;
  name: string;
  status: 'connected' | 'disconnected' | 'error';
  activeBots: number;
  balance: number;
  currency: string;
  last24hVolume: number;
  latencyMs?: number;
};

type WalletSummaryCard = {
  id: string;
  name: string;
  status: 'connected' | 'disconnected' | 'error';
  network: string;
  balance: number;
  currency: string;
  latencyMs?: number;
};

type WalletBalanceRecord = {
  assetSymbol?: string | null;
  asset_symbol?: string | null;
  quantity?: number | string | null;
  valueUsd?: number | string | null;
  value_usd?: number | string | null;
  valueChf?: number | string | null;
  value_chf?: number | string | null;
  snapshotAt?: string | null;
  snapshot_at?: string | null;
};

type WalletBalanceSummary = {
  balance: number;
  currency: string;
  balanceLabel?: string;
  balanceHint?: string;
};

interface ActivityItem {
  id: string;
  type: 'online' | 'warning' | 'error';
  message: string;
  details: string;
  pnl?: number;
  timestamp: string;
}

interface OverviewApprovalItem {
  approvalUuid: string;
  title: string;
  description: string;
  requester: string;
  timestamp: string;
}

const renderAlertSeverityBadge = (severity: string) => {
  const severityLower = severity.toLowerCase();
  switch (severityLower) {
    case 'critical':
      return <Badge variant="destructive">Critical</Badge>;
    case 'high':
      return <Badge className="bg-amber-600 text-white">High</Badge>;
    case 'medium':
      return <Badge className="bg-amber-100 text-amber-800">Medium</Badge>;
    case 'low':
    default:
      return <Badge variant="secondary">Low</Badge>;
  }
};

const parseConnectionNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const normalized = trimmed.replace(/[\s,]+/g, '').replace(/[^0-9.+-]/g, '');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const extractBaseAsset = (symbol: string): string | null => {
  const trimmed = symbol.trim();
  if (!trimmed) return null;
  const tokens = trimmed.split(/[/:_-]/).filter((token) => token.length > 0);
  if (tokens.length === 0) return null;
  return tokens[0].toUpperCase();
};

const resolveTradeQuantityDecimals = (quantity: number): number => {
  const absValue = Math.abs(quantity);
  if (absValue >= 1_000) return 0;
  if (absValue >= 10) return 2;
  if (absValue >= 1) return 4;
  if (absValue >= 0.01) return 4;
  if (absValue >= 0.0001) return 6;
  return 8;
};

const formatTradeQuantity = (quantity: number, locale: string): string => {
  const absValue = Math.abs(quantity);
  if (absValue < TRADE_QUANTITY_ZERO_EPSILON) {
    return '0';
  }

  const decimals = resolveTradeQuantityDecimals(quantity);
  const roundedAbs = Number(absValue.toFixed(decimals));
  if (roundedAbs === 0 && absValue > 0) {
    const threshold = 10 ** -decimals;
    return `<${formatNumber(threshold, { locale, decimals, sign: 'never' })}`;
  }

  return formatNumber(quantity, {
    locale,
    decimals,
    sign: 'never',
  });
};

const resolveConnectionName = (connection: ConnectionRecord): string => {
  if (typeof connection.label === 'string' && connection.label.trim().length > 0) {
    return connection.label.trim();
  }

  const metadata = (connection.metadata ?? {}) as Record<string, unknown>;
  const candidates = [
    metadata['label'],
    metadata['name'],
    metadata['venue'],
    metadata['exchange'],
    metadata['provider'],
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }

  if (Array.isArray(connection.tags) && connection.tags.length > 0) {
    return connection.tags[0];
  }

  const fallbackId =
    (typeof connection.connectionUuid === 'string' && connection.connectionUuid) ||
    (connection.id ? String(connection.id) : undefined);
  const legacy = connection.legacyId ?? fallbackId ?? 'unknown';
  return `Connection ${legacy}`;
};

const normalizeConnectionStatus = (status?: string | null): 'connected' | 'disconnected' | 'error' => {
  const normalized = (status ?? '').toString().toLowerCase();
  if (!normalized) {
    return 'disconnected';
  }

  if (['error', 'failed', 'degraded', 'issue', 'halted', 'warning'].some((value) => normalized.includes(value))) {
    return 'error';
  }

  if (
    [
      'connected',
      'active',
      'online',
      'enabled',
      'running',
      'live',
      'ok',
      'healthy',
      'ready',
      'available',
    ].some((value) => normalized.includes(value))
  ) {
    return 'connected';
  }

  return 'disconnected';
};

const normalizeConnectionType = (connection: ConnectionRecord): 'exchange' | 'wallet' | 'other' => {
  const directType = (
    connection.type ??
    connection.connectionType ??
    connection.connection_type ??
    ''
  )
    .toString()
    .toLowerCase()
    .trim();
  if (directType === 'exchange') return 'exchange';
  if (directType === 'wallet') return 'wallet';

  const metadata = (connection.metadata ?? {}) as Record<string, unknown>;
  const metadataType = (metadata.type ?? metadata.connectionType ?? metadata.connection_type ?? '').toString().toLowerCase().trim();
  if (metadataType === 'exchange') return 'exchange';
  if (metadataType === 'wallet') return 'wallet';

  return 'other';
};

const resolveConnectionActiveBots = (metadata: Record<string, unknown> | null | undefined): number => {
  if (!metadata) return 0;
  const data = metadata as Record<string, unknown>;
  const candidates = [
    data['activeBots'],
    data['active_bots'],
    data['botsActive'],
    data['bots_active'],
    data['runningBots'],
    data['running_bots'],
    data['botCount'],
    data['bots'],
    data['activeBotCount'],
  ];

  for (const candidate of candidates) {
    const parsed = parseConnectionNumber(candidate);
    if (parsed !== null) {
      return Math.max(0, Math.round(parsed));
    }
  }
  return 0;
};

const resolveConnectionBalance = (metadata: Record<string, unknown> | null | undefined): number => {
  if (!metadata) return 0;
  const data = metadata as Record<string, unknown>;
  const chfKeys = [
    'totalBalanceChf',
    'total_balance_chf',
    'valueChf',
    'value_chf',
  ];
  for (const key of chfKeys) {
    const parsed = parseConnectionNumber(data[key]);
    if (parsed !== null) {
      return parsed;
    }
  }

  const totalBalanceCurrency = data['totalBalanceCurrency'] ?? data['total_balance_currency'];
  const genericKeys = [
    'totalBalance',
    'total_balance',
  ];

  for (const key of genericKeys) {
    if (typeof totalBalanceCurrency === 'string' && totalBalanceCurrency.trim().toUpperCase() !== 'CHF') {
      continue;
    }
    const value = data[key];
    const parsed = parseConnectionNumber(value);
    if (parsed !== null) {
      return parsed;
    }
  }

  const balances = data['balances'];
  if (Array.isArray(balances)) {
    let sumChf = 0;
    let hasChf = false;
    balances.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const typedEntry = entry as Record<string, unknown>;
      const chf =
        parseConnectionNumber(typedEntry.valueChf) ??
        parseConnectionNumber(typedEntry.valueCHF) ??
        parseConnectionNumber(typedEntry.value_chf);
      if (chf !== null) {
        sumChf += chf;
        hasChf = true;
      }
    });

    if (hasChf) return sumChf;
  }

  return 0;
};

const resolveConnectionCurrency = (metadata: Record<string, unknown> | null | undefined): string => {
  if (!metadata) return 'CHF';
  const data = metadata as Record<string, unknown>;
  const candidates = [
    data['totalBalanceCurrency'],
    data['total_balance_currency'],
    data['currency'],
    data['baseCurrency'],
    data['base_currency'],
    data['ccy'],
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim().toUpperCase();
    }
  }

  return 'CHF';
};

const resolveConnectionVolume = (metadata: Record<string, unknown> | null | undefined): number => {
  if (!metadata) return 0;
  const data = metadata as Record<string, unknown>;
  const candidates = [
    data['volume24h'],
    data['volume_24h'],
    data['last24hVolume'],
    data['volume'],
  ];

  for (const candidate of candidates) {
    const parsed = parseConnectionNumber(candidate);
    if (parsed !== null) {
      return parsed;
    }
  }
  return 0;
};

const resolveConnectionLatency = (
  metadata: Record<string, unknown> | null | undefined,
): number | undefined => {
  if (!metadata) return undefined;
  const data = metadata as Record<string, unknown>;
  const latency =
    parseConnectionNumber(data.latencyMs) ??
    parseConnectionNumber(data.latency_ms) ??
    parseConnectionNumber(data.pingMs) ??
    parseConnectionNumber(data.ping_ms);
  if (latency === null) return undefined;
  const rounded = Math.round(latency);
  return rounded >= 0 ? rounded : undefined;
};

const formatConnectionNetwork = (network: string): string =>
  network
    .trim()
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => (token.length <= 4 ? token.toUpperCase() : `${token[0].toUpperCase()}${token.slice(1).toLowerCase()}`))
    .join(' ');

const resolveConnectionNetwork = (
  connection: ConnectionRecord,
  metadata: Record<string, unknown> | null | undefined,
): string => {
  const data = metadata as Record<string, unknown> | undefined;
  const candidates = [
    connection.network,
    data?.network,
    data?.chain,
    data?.networkName,
    data?.network_name,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return formatConnectionNetwork(candidate);
    }
  }

  return 'Unknown network';
};

export {
  arraysEqual,
  renderAlertSeverityBadge,
  parseConnectionNumber,
  extractBaseAsset,
  resolveTradeQuantityDecimals,
  formatTradeQuantity,
  resolveConnectionName,
  normalizeConnectionStatus,
  normalizeConnectionType,
  resolveConnectionActiveBots,
  resolveConnectionBalance,
  resolveConnectionCurrency,
  resolveConnectionVolume,
  resolveConnectionLatency,
  formatConnectionNetwork,
  resolveConnectionNetwork,
};

export default function Overview() {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [streamStatus, setStreamStatus] = useState<LiveStatus>('connecting');
  const [timeRange, setTimeRange] = useState<TimeRangeState>({ value: DEFAULT_TIME_RANGE });
  const [isKpiManagerOpen, setIsKpiManagerOpen] = useState(false);
  const [managerKpis, setManagerKpis] = useState<KPI[]>([]);
  const [managerLoading, setManagerLoading] = useState(false);
  const { scopeReady, tenant, organization, portfolio, mode } = useScope();
  const { toast } = useToast();
  const rbac = useRBAC();
  const canApprove = rbac.can('approve_changes');
  const canManageKpis = rbac.can('manage_kpis') || rbac.hasRole('analyst');
  const isMobile = useIsMobile(1024);
  const tenantLocale = useMemo(
    () => ensureLocale(typeof tenant?.locale === 'string' ? tenant?.locale : undefined),
    [tenant?.locale],
  );
  const [, navigate] = useLocation();
  const handleManageConnections = useCallback(() => navigate('/connections'), [navigate]);
  const [selectedKpiIds, setSelectedKpiIds] = useState<string[]>([]);
  const selectedKpiIdsRef = useRef<string[]>([]);
  const [selectionSaving, setSelectionSaving] = useState(false);
  const [isKillSwitchDialogOpen, setIsKillSwitchDialogOpen] = useState(false);
  const [killSwitchInput, setKillSwitchInput] = useState('');
  const [killSwitchLoading, setKillSwitchLoading] = useState(false);
  const [killSwitchError, setKillSwitchError] = useState<string | null>(null);

  useEffect(() => {
    selectedKpiIdsRef.current = selectedKpiIds;
  }, [selectedKpiIds]);
  const [volumeView, setVolumeView] = useState<'chf' | 'trades'>('chf');
  const [openAlertsExpanded, setOpenAlertsExpanded] = useState(true);
  const [resolvedAlertsExpanded, setResolvedAlertsExpanded] = useState(false);
  const [showOpenOlder, setShowOpenOlder] = useState(false);
  const [showResolvedOlder, setShowResolvedOlder] = useState(false);
  const [notificationBarDismissed, setNotificationBarDismissed] = useState(false);
  const [activityFilter, setActivityFilter] = useState<FeedItemType | 'all'>('all');

  const handleTimeRangeChange = useCallback((range: TimeRangeData) => {
    setTimeRange({
      value: range.value,
      from: range.from.toISOString(),
      to: range.to.toISOString(),
    });
  }, []);

  const customRangeDates = useMemo(() => {
    if (timeRange.value !== 'custom' || !timeRange.from || !timeRange.to) {
      return undefined;
    }
    return {
      from: new Date(timeRange.from),
      to: new Date(timeRange.to),
    };
  }, [timeRange]);

  const viewLogAction = useMemo(
    () => ({
      label: 'View log',
      altText: 'Open diagnostics log',
      onClick: () => {
        if (typeof window !== 'undefined') {
          window.open('/admin?section=logs', '_blank', 'noopener,noreferrer');
        }
      },
    }),
    [],
  );

  const settingsQuery = useQuery<SettingsResponse>({
    queryKey: ['settings', organization?.id ?? 'org', tenant?.id ?? 'tenant'],
    enabled: scopeReady,
    staleTime: 5 * 60 * 1000,
    queryFn: () => settingsAPI.get(organization?.id ? { orgId: organization.id } : undefined),
  });
  const { refetch: refetchSettings } = settingsQuery;

  const showErrorToast = useCallback(
    (title: string, description: string) => {
      toast({
        title,
        description,
        tone: 'error',
        duration: 5000,
        action: viewLogAction,
      });
    },
    [toast, viewLogAction],
  );

  const approvalsQuery = useQuery<ApprovalsListResponse>({
    queryKey: ['overview-approvals', tenant?.id, organization?.id],
    enabled: scopeReady && canApprove,
    queryFn: () => approvalsAPI.list(),
  });

  const approvalsList = useMemo<OverviewApprovalItem[]>(() => {
    if (!canApprove) return [];
    const approvals: ApprovalRecord[] = approvalsQuery.data?.approvals ?? [];
    return approvals
      .filter((approval) => approval.status === 'pending')
      .map((approval) => ({
        approvalUuid: approval.approvalUuid,
        title: APPROVAL_TYPE_LABELS[approval.type] ?? approval.type,
        description: approval.notes ?? 'Awaiting review',
        requester: approval.requestedBy ?? 'Unknown requester',
        timestamp: approval.requestedAt,
      }))
      .slice(0, 3);
  }, [approvalsQuery.data, canApprove]);

  const handleBatchAction = useCallback(
    (action: 'approve' | 'reject') => {
      if (!approvalsList.length) {
        return;
      }
      toast({
        title: action === 'approve' ? 'Batch approve queued' : 'Batch reject queued',
        description: `${approvalsList.length} approvals marked for ${action}.`,
        tone: 'info',
      });
    },
    [approvalsList.length, toast],
  );

  const handleApprovalAction = useCallback(
    (action: 'approve' | 'reject', approval: OverviewApprovalItem) => {
      toast({
        title: action === 'approve' ? 'Approval queued' : 'Rejection queued',
        description: `${approval.title} marked for ${action}.`,
        tone: action === 'approve' ? 'success' : 'warning',
      });
    },
    [toast],
  );

  const getErrorMessage = useCallback((error: unknown) => {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'Unexpected error';
  }, []);

  const handleKpiSelectionChange = useCallback(
    async (nextIds: string[]) => {
      const previous = selectedKpiIdsRef.current;
      setSelectedKpiIds(nextIds);
      setSelectionSaving(true);
      try {
        await settingsAPI.update({
          updates: [
            {
              scope: 'tenant',
              namespace: 'trading',
              values: { dashboardKpis: nextIds },
              orgId: organization?.id ?? null,
            },
          ],
        });
        selectedKpiIdsRef.current = nextIds;
        void refetchSettings();
      } catch (error) {
        setSelectedKpiIds(previous);
        const message = getErrorMessage(error);
        toast({
          title: 'Failed to update dashboard KPIs',
          description: message,
          tone: 'error',
        });
        throw error instanceof Error ? error : new Error(message);
      } finally {
        setSelectionSaving(false);
      }
    },
    [getErrorMessage, organization?.id, refetchSettings, toast],
  );

  const handleKillSwitchSubmit = useCallback(async () => {
    const confirmation = killSwitchInput.trim().toUpperCase();
    if (confirmation !== KILL_SWITCH_CONFIRMATION) {
      setKillSwitchError('Enter the exact confirmation phrase to continue.');
      return;
    }
    setKillSwitchError(null);
    setKillSwitchLoading(true);
    try {
      await killSwitchAPI.engage({
        scope: 'tenant',
        profile: 'hard',
        reason: 'Manual kill switch engagement from Overview dashboard.',
      });
      toast({
        title: 'Kill switch engaged',
        description: 'All agents have been paused until the kill switch is cleared.',
        tone: 'warning',
      });
      setIsKillSwitchDialogOpen(false);
      setKillSwitchInput('');
    } catch (error) {
      const message = getErrorMessage(error);
      setKillSwitchError(message);
      toast({
        title: 'Failed to engage kill switch',
        description: message,
        tone: 'error',
      });
    } finally {
      setKillSwitchLoading(false);
    }
  }, [getErrorMessage, killSwitchInput, toast]);

  useEffect(() => {
    if (!isKillSwitchDialogOpen) {
      setKillSwitchInput('');
      setKillSwitchError(null);
      setKillSwitchLoading(false);
    }
  }, [isKillSwitchDialogOpen]);

  useEffect(() => {
    if (killSwitchError) {
      setKillSwitchError(null);
    }
  }, [killSwitchInput]);

  const overviewQuery = useQuery({
    queryKey: [
      'overview',
      tenant?.id ?? 'tenant',
      organization?.id ?? 'org',
      portfolio?.id ?? 'portfolio',
      mode?.id ?? 'mode',
      timeRange.value,
      timeRange.from ?? '',
      timeRange.to ?? '',
    ],
    enabled: scopeReady,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: false,
    retry: 2,
    queryFn: () =>
      overviewAPI.getDashboard({
        range: timeRange.value,
        from: timeRange.value === 'custom' ? timeRange.from : undefined,
        to: timeRange.value === 'custom' ? timeRange.to : undefined,
        organizationId: organization?.id,
        portfolioId: portfolio?.id,
        mode: mode?.name,
      }) as Promise<OverviewResponse>,
  });

  const {
    data: connectionRecords = [],
    isLoading: connectionsLoading,
    isFetching: connectionsFetching,
    isError: connectionsError,
    error: connectionsErrorData,
    refetch: refetchConnections,
  } = useQuery<ConnectionRecord[]>({
    queryKey: ['overview-connections', tenant?.id ?? 'tenant'],
    enabled: scopeReady,
    queryFn: async () => {
      const [exchangeRows, walletRows] = await Promise.all([
        connectionsAPI.list({ type: 'exchange', includeInactive: true }),
        connectionsAPI.list({ type: 'wallet', includeInactive: true }),
      ]);

      const combined = [
        ...(Array.isArray(exchangeRows) ? exchangeRows : []),
        ...(Array.isArray(walletRows) ? walletRows : []),
      ] as ConnectionRecord[];

      const deduped = new Map<string, ConnectionRecord>();
      combined.forEach((row, index) => {
        const key =
          row.connectionUuid ??
          (row.id ? String(row.id) : `connection-${index}`);
        if (!deduped.has(key)) {
          deduped.set(key, row);
        }
      });

      return Array.from(deduped.values());
    },
    staleTime: 1000 * 60 * 5,
  });

  const overviewData = overviewQuery.data;
  const { refetch: refetchOverview } = overviewQuery;
  const isRefetching = overviewQuery.isFetching && !overviewQuery.isLoading;

  useEffect(() => {
    if (isRefetching) {
      setStreamStatus('connecting');
    }
  }, [isRefetching]);

  useEffect(() => {
    if (!scopeReady) {
      return;
    }

    let socket: WebSocket | null = null;
    let pollTimer: number | undefined;

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = window.setInterval(() => {
        void refetchOverview();
      }, OVERVIEW_FALLBACK_POLL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
    };

    const wsUrl = import.meta.env.VITE_WS_OVERVIEW_URL as string | undefined;

    if (wsUrl) {
      try {
        const params = new URLSearchParams();
        if (organization?.id) params.append('organizationId', organization.id);
        if (portfolio?.id) params.append('portfolioId', portfolio.id);
        if (mode?.name) params.append('mode', mode.name);
        if (timeRange.value === 'custom') {
          if (timeRange.from) params.append('from', timeRange.from);
          if (timeRange.to) params.append('to', timeRange.to);
        } else {
          params.append('range', timeRange.value);
        }
        const urlWithParams = params.toString()
          ? `${wsUrl}${wsUrl.includes('?') ? '&' : '?'}${params}`
          : wsUrl;

        socket = new WebSocket(urlWithParams);
        socket.addEventListener('open', () => {
          stopPolling();
        });
        socket.addEventListener('message', () => {
          void refetchOverview();
        });
        const handleSocketError = () => {
          startPolling();
        };
        socket.addEventListener('error', handleSocketError);
        socket.addEventListener('close', handleSocketError);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Failed to open overview websocket, falling back to polling', error);
        }
        startPolling();
      }
    } else {
      startPolling();
    }

    return () => {
      socket?.close();
      stopPolling();
    };
  }, [scopeReady, organization?.id, portfolio?.id, mode?.name, timeRange.value, timeRange.from, timeRange.to, refetchOverview]);

  useEffect(() => {
    if (overviewQuery.isSuccess && overviewQuery.dataUpdatedAt > 0) {
      setLastUpdated(new Date(overviewQuery.dataUpdatedAt));
      setStreamStatus('connected');
    }
  }, [overviewQuery.dataUpdatedAt, overviewQuery.isSuccess]);

  const refreshManagerKpis = useCallback(async () => {
    setManagerLoading(true);
    try {
      const data = await kpiAPI.list({ includeHistory: true, historyLimit: 60 });
      setManagerKpis(data);
    } catch (error) {
      console.error('Failed to load KPI catalog', error);
      showErrorToast('Unable to load KPIs', getErrorMessage(error));
    } finally {
      setManagerLoading(false);
    }
  }, [getErrorMessage, showErrorToast]);

  useEffect(() => {
    if (isKpiManagerOpen) {
      void refreshManagerKpis();
    }
  }, [isKpiManagerOpen, refreshManagerKpis]);

  const handleCreateKpi = useCallback(
    async (payload: KPIUpsertPayload) => {
      try {
        await kpiAPI.create(payload);
        toast({
          title: 'KPI created',
          description: `"${payload.label}" is ready for review.`,
          tone: 'success',
        });
        await refreshManagerKpis();
        await refetchOverview();
      } catch (error) {
        showErrorToast('Failed to create KPI', getErrorMessage(error));
        throw error;
      }
    },
    [getErrorMessage, refreshManagerKpis, refetchOverview, showErrorToast, toast],
  );

  const handleUpdateKpi = useCallback(
    async (id: string, payload: KPIUpsertPayload) => {
      try {
        await kpiAPI.update(id, payload);
        toast({
          title: 'KPI updated',
          description: `"${payload.label}" draft saved.`,
          tone: 'success',
        });
        await refreshManagerKpis();
        await refetchOverview();
      } catch (error) {
        showErrorToast('Failed to update KPI', getErrorMessage(error));
        throw error;
      }
    },
    [getErrorMessage, refreshManagerKpis, refetchOverview, showErrorToast, toast],
  );

  const handlePublishKpi = useCallback(
    async (id: string) => {
      try {
        await kpiAPI.publish(id);
        toast({
          title: 'KPI published',
          description: 'Live dashboards now reflect the updated definition.',
          tone: 'success',
        });
        await refreshManagerKpis();
        await refetchOverview();
      } catch (error) {
        showErrorToast('Failed to publish KPI', getErrorMessage(error));
      }
    },
    [getErrorMessage, refreshManagerKpis, refetchOverview, showErrorToast, toast],
  );

  const handleRetireKpi = useCallback(
    async (id: string) => {
      try {
        await kpiAPI.retire(id);
        toast({
          title: 'KPI retired',
          description: 'The KPI is now removed from tenant dashboards.',
          tone: 'success',
        });
        await refreshManagerKpis();
        await refetchOverview();
      } catch (error) {
        showErrorToast('Failed to retire KPI', getErrorMessage(error));
      }
    },
    [getErrorMessage, refreshManagerKpis, refetchOverview, showErrorToast, toast],
  );

  const handleDeleteKpi = useCallback(
    async (id: string) => {
      try {
        await kpiAPI.remove(id);
        toast({
          title: 'KPI deleted',
          description: 'The KPI definition and history were removed.',
          tone: 'success',
        });
        await refreshManagerKpis();
        await refetchOverview();
      } catch (error) {
        showErrorToast('Failed to delete KPI', getErrorMessage(error));
      }
    },
    [getErrorMessage, refreshManagerKpis, refetchOverview, showErrorToast, toast],
  );

  const kpis = useMemo(() => overviewData?.kpis ?? [], [overviewData?.kpis]);
  const metricsSeries = useMemo(
    () => overviewData?.metrics?.portfolio ?? [],
    [overviewData?.metrics?.portfolio],
  );

  const defaultKpiSelection = useMemo(() => {
    if (!kpis.length) {
      return [] as string[];
    }
    return [...kpis]
      .sort(
        (a, b) =>
          (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.sortOrder ?? Number.MAX_SAFE_INTEGER),
      )
      .slice(0, MAX_KPI_SELECTION)
      .map((item) => item.id);
  }, [kpis]);

  const storedKpiSelection = useMemo(() => {
    const tradingValues = (settingsQuery.data?.effective?.trading?.values ?? {}) as Record<string, unknown>;
    const stored = tradingValues.dashboardKpis;
    if (Array.isArray(stored)) {
      return stored.map((value) => String(value));
    }
    return [] as string[];
  }, [settingsQuery.data]);

  useEffect(() => {
    if (!settingsQuery.isFetched) {
      return;
    }
    const next = storedKpiSelection.length ? storedKpiSelection : defaultKpiSelection;
    if (!next.length) {
      return;
    }
    setSelectedKpiIds((prev) => (prev.length && arraysEqual(prev, next) ? prev : next.slice(0, MAX_KPI_SELECTION)));
  }, [storedKpiSelection, defaultKpiSelection, settingsQuery.isFetched]);

  useEffect(() => {
    if (!kpis.length) {
      return;
    }
    const validIds = new Set(kpis.map((kpi) => kpi.id));
    setSelectedKpiIds((prev) => {
      const filtered = prev.filter((id) => validIds.has(id));
      if (filtered.length === prev.length) {
        return prev;
      }
      const extras = defaultKpiSelection.filter((id) => validIds.has(id) && !filtered.includes(id));
      const next = [...filtered, ...extras].slice(0, MAX_KPI_SELECTION);
      return next;
    });
  }, [kpis, defaultKpiSelection]);

  const datasetTrades = overviewData?.trades ?? [];
  const datasetAlerts = overviewData?.alerts ?? [];
  const botsSummary = overviewData?.bots;

  const { tradeActivities, systemActivities } = useMemo(() => {
    const trades: ActivityItem[] = datasetTrades.slice(0, 6).map((trade) => {
      const tradeKey = trade.tradeUuid ?? trade.id;
      const type: ActivityItem['type'] = trade.pnl < 0 ? 'warning' : 'online';
      const formattedQuantity = formatTradeQuantity(trade.quantity, tenantLocale);
      const baseAsset = extractBaseAsset(trade.symbol);
      const quantityLabel = baseAsset ? `${formattedQuantity} ${baseAsset}` : formattedQuantity;
      const formattedPrice = formatCurrency(trade.price, {
        locale: tenantLocale,
        decimals: 2,
        sign: 'never',
      });
      return {
        id: tradeKey,
        type,
        message: `Trade ${trade.symbol}`,
        details: `${trade.side.toUpperCase()} ${quantityLabel} @ ${formattedPrice}`,
        pnl: Math.abs(trade.pnl) >= TRADE_PNL_DISPLAY_EPSILON ? trade.pnl : undefined,
        timestamp: trade.executedAt,
      };
    });

    const systems: ActivityItem[] = datasetAlerts
      .slice(0, 6)
      .map((alert) => {
        const severity = alert.severity.toLowerCase();
        const type: ActivityItem['type'] = severity === 'critical' || severity === 'high' ? 'error' : 'warning';
        return {
          id: alert.id,
          type,
          message: alert.title,
          details: alert.description,
          timestamp: alert.createdAt,
        };
      })
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return {
      tradeActivities: trades,
      systemActivities: systems,
    };
  }, [datasetAlerts, datasetTrades, tenantLocale]);

  const alertsGrouping = useMemo(() => {
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const baseGrouping = {
      open: { recent: [] as OverviewAlertResponse[], older: [] as OverviewAlertResponse[] },
      resolved: { recent: [] as OverviewAlertResponse[], older: [] as OverviewAlertResponse[] },
    };

    datasetAlerts.forEach((alert) => {
      const status = (alert.status ?? '').toLowerCase();
      const isResolved = status === 'resolved' || status === 'closed';
      const bucket = isResolved ? baseGrouping.resolved : baseGrouping.open;
      const createdAt = new Date(alert.createdAt).getTime();
      const isOlder = Number.isFinite(createdAt) ? now - createdAt > ninetyDaysMs : false;
      if (isOlder) {
        bucket.older.push(alert);
      } else {
        bucket.recent.push(alert);
      }
    });

    return baseGrouping;
  }, [datasetAlerts]);
  useEffect(() => {
    if (!alertsGrouping.open.older.length) {
      setShowOpenOlder(false);
    }
    if (!alertsGrouping.resolved.older.length) {
      setShowResolvedOlder(false);
    }
  }, [alertsGrouping.open.older.length, alertsGrouping.resolved.older.length]);

  const kpisLoading = overviewQuery.isLoading && kpis.length === 0;
  const activitiesLoading =
    overviewQuery.isLoading && tradeActivities.length === 0 && systemActivities.length === 0;
  const hasRecentActivity = tradeActivities.length > 0 || systemActivities.length > 0;

  const alertCounts = useMemo(() => {
    const openAlerts = [...alertsGrouping.open.recent, ...alertsGrouping.open.older];
    return {
      critical: openAlerts.filter((a) => a.severity.toLowerCase() === 'critical').length,
      high: openAlerts.filter((a) => a.severity.toLowerCase() === 'high').length,
      medium: openAlerts.filter((a) => a.severity.toLowerCase() === 'medium').length,
      low: openAlerts.filter((a) => a.severity.toLowerCase() === 'low').length,
      total: openAlerts.length,
    };
  }, [alertsGrouping]);

  const notificationBarItems = useMemo<AlertNotificationItem[]>(() => {
    const items: AlertNotificationItem[] = [];
    if (canApprove && approvalsList.length > 0) {
      items.push({
        id: 'approvals',
        type: 'approval',
        count: approvalsList.length,
        label: `${approvalsList.length} Pending Approval${approvalsList.length !== 1 ? 's' : ''}`,
        onAction: () => navigate('/approvals'),
        actionLabel: 'Review',
      });
    }
    if (alertCounts.critical > 0) {
      items.push({
        id: 'critical-alerts',
        type: 'alert',
        severity: 'critical',
        count: alertCounts.critical,
        label: `${alertCounts.critical} Critical Alert${alertCounts.critical !== 1 ? 's' : ''}`,
        onAction: () => setOpenAlertsExpanded(true),
        actionLabel: 'View',
      });
    }
    if (alertCounts.high > 0 && alertCounts.critical === 0) {
      items.push({
        id: 'high-alerts',
        type: 'alert',
        severity: 'high',
        count: alertCounts.high,
        label: `${alertCounts.high} High Alert${alertCounts.high !== 1 ? 's' : ''}`,
        onAction: () => setOpenAlertsExpanded(true),
        actionLabel: 'View',
      });
    }
    return items;
  }, [canApprove, approvalsList.length, alertCounts, navigate]);

  const liveFeedItems = useMemo<LiveFeedItem[]>(() => {
    const items: LiveFeedItem[] = [];

    // Add pending approvals as action_required items
    approvalsList.forEach((approval) => {
      items.push({
        id: `approval-${approval.approvalUuid}`,
        type: 'alert',
        severity: 'warning',
        title: approval.title,
        details: `${approval.description} - submitted by ${approval.requester}`,
        timestamp: approval.timestamp,
        actionRequired: true,
        actionStatus: 'pending',
        actionLabel: 'Review',
        onAction: () => navigate('/approvals'),
      });
    });

    tradeActivities.forEach((trade) => {
      items.push({
        id: trade.id,
        type: 'trade',
        title: trade.message,
        details: trade.details,
        pnl: trade.pnl,
        timestamp: trade.timestamp,
        botName: (datasetTrades.find((t) => (t.tradeUuid ?? t.id) === trade.id)?.botId) ?? undefined,
      });
    });

    systemActivities.forEach((event) => {
      items.push({
        id: event.id,
        type: 'system',
        severity: event.type === 'error' ? 'error' : event.type === 'warning' ? 'warning' : 'info',
        title: event.message,
        details: event.details,
        timestamp: event.timestamp,
      });
    });

    return items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [tradeActivities, systemActivities, datasetTrades, approvalsList, navigate]);

  const liveFeedSummary = useMemo<LiveFeedSummary>(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayTrades = datasetTrades.filter((t) => new Date(t.executedAt) >= todayStart);
    const totalPnl = todayTrades.reduce((sum, t) => sum + t.pnl, 0);
    const winCount = todayTrades.filter((t) => t.pnl > 0).length;
    return {
      tradeCount: todayTrades.length,
      totalPnl,
      winRate: todayTrades.length > 0 ? (winCount / todayTrades.length) * 100 : undefined,
      pendingActions: approvalsList.length,
    };
  }, [datasetTrades, approvalsList.length]);

  const activitySummary = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayTrades = datasetTrades.filter((t) => new Date(t.executedAt) >= todayStart);
    const totalPnl = todayTrades.reduce((sum, t) => sum + t.pnl, 0);
    const winCount = todayTrades.filter((t) => t.pnl > 0).length;
    return {
      tradeCount: todayTrades.length,
      totalPnl,
      winRate: todayTrades.length > 0 ? (winCount / todayTrades.length) * 100 : undefined,
    };
  }, [datasetTrades]);

  const formattedLastUpdated = useMemo(
    () =>
      lastUpdated.toLocaleTimeString('en-GB', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'UTC',
      }),
    [lastUpdated],
  );

  const kpiCards = useMemo<OverviewKpiCardProps[]>(() => {
    if (kpis.length === 0) {
      return [];
    }

    const cardsById = new Map<string, OverviewKpiCardProps>();
    const byId = new Map(kpis.map((kpi) => [String(kpi.id ?? '').toLowerCase(), kpi] as const));
    const byLabel = new Map(kpis.map((kpi) => [String(kpi.label ?? '').toLowerCase(), kpi] as const));

    const findKpi = (ids: string[], labels: string[]) => {
      for (const id of ids) {
        const found = byId.get(id.toLowerCase());
        if (found) return found;
      }
      for (const label of labels) {
        const found = byLabel.get(label.toLowerCase());
        if (found) return found;
      }
      return null;
    };

    const formatValuationSource = (source: string | undefined) => {
      const normalized = (source ?? '').trim().toLowerCase();
      if (!normalized) return 'unknown source';
      if (normalized === 'coingecko') return 'CoinGecko';
      if (normalized === 'internal_snapshot') return 'internal snapshot';
      if (normalized === 'stablecoin_parity') return 'stablecoin parity';
      if (normalized === 'provider_quote') return 'provider quote';
      if (normalized === 'usd_fx_conversion') return 'USD→CHF FX conversion';
      if (normalized === 'connection_snapshot') return 'connection snapshot';
      if (normalized === 'mixed') return 'mixed sources';
      return normalized.replace(/_/g, ' ');
    };

    const buildValuationFootnote = (kpi: KPI): OverviewKpiFootnote | null => {
      const valuation = kpi.valuation ?? null;
      if (!valuation) return null;
      const asOf = typeof valuation.asOf === 'string' && valuation.asOf ? valuation.asOf : null;
      const relative = asOf ? formatRelativeTime(asOf) : 'time unavailable';
      const sourceLabel = formatValuationSource(valuation.source);
      const coverage = typeof valuation.coveragePct === 'number'
        ? formatPercent(valuation.coveragePct, { locale: tenantLocale, decimals: 0, sign: 'never' })
        : '—';
      const drift =
        typeof valuation.driftAbs === 'number'
          ? formatCurrency(valuation.driftAbs, { locale: tenantLocale, decimals: 2, sign: 'never' })
          : '—';
      const tone: FootnoteTone = valuation.status === 'warning' ? 'negative' : 'neutral';
      return {
        label: 'Valuation',
        value: `${sourceLabel} · ${relative}`,
        tone,
        tooltip: `Coverage ${coverage} · Drift ${drift}`,
      };
    };

    const buildValuationChip = (kpi: KPI): OverviewKpiChip | undefined => {
      const status = (kpi.valuation?.status ?? '').toLowerCase();
      if (status === 'warning') {
        return { label: 'Drift warning', tone: 'negative' };
      }
      if (status === 'partial') {
        return { label: 'Partial valuation', tone: 'neutral' };
      }
      if (status === 'unpriced') {
        return { label: 'Unpriced assets', tone: 'neutral' };
      }
      return undefined;
    };

    const resolveCurrencyDecimals = (input: number | null) => {
      if (input === null) return 0;
      return Math.abs(input) < 10_000 ? 2 : 0;
    };

    const formatPrimaryValue = (kpi: KPI, value: number | null) => {
      if (value === null) {
        return '—';
      }
      switch (kpi.type) {
        case 'currency':
          return formatCurrency(value, {
            locale: tenantLocale,
            decimals: resolveCurrencyDecimals(value),
            sign: 'never',
          });
        case 'percentage':
          return formatPercent(value, { locale: tenantLocale, decimals: 2, sign: 'never' });
        case 'number':
        case 'time':
        default:
          return formatNumber(value, { locale: tenantLocale, decimals: 0, sign: 'never' });
      }
    };

    const buildGenericCard = (kpi: KPI) => {
      const metrics = computeHistoryMetrics(kpi);
      const valueText = formatPrimaryValue(kpi, metrics.currentValue);
      const valueTooltip =
        metrics.currentValue === null
          ? undefined
          : thinSpace(
              kpi.type === 'currency'
                ? formatCurrency(metrics.currentValue, {
                    locale: tenantLocale,
                    decimals: 2,
                    sign: 'never',
                  })
                : formatPrimaryValue(kpi, metrics.currentValue),
            );

      const footnotes: OverviewKpiFootnote[] = [];
      if (kpi.type === 'currency') {
        footnotes.push(
          buildCurrencyFootnote('Δ 24h', metrics.deltaAbsolute, metrics.deltaPercent, tenantLocale),
        );
        footnotes.push(
          buildCurrencyFootnote(
            'vs 7-day avg',
            metrics.deltaVsAverageAbsolute,
            metrics.deltaVsAveragePercent,
            tenantLocale,
            { neutralTone: true },
          ),
        );
      } else if (kpi.type === 'percentage') {
        footnotes.push(
          buildPercentFootnote('Δ 24h', metrics.deltaPercent, 2, tenantLocale),
        );
        footnotes.push(
          buildPercentFootnote('vs 7-day avg', metrics.deltaVsAveragePercent, 2, tenantLocale, {
            neutralTone: true,
          }),
        );
      } else {
        footnotes.push(
          buildNumberFootnote('Δ 24h', metrics.deltaAbsolute, 2, tenantLocale),
        );
        footnotes.push(
          buildNumberFootnote('vs 7-day avg', metrics.deltaVsAverageAbsolute, 2, tenantLocale, {
            neutralTone: true,
          }),
        );
      }

      return {
        title: kpi.label,
        value: valueText,
        valueTooltip,
        footnotes,
      };
    };

    const registerCard = (kpi: KPI, card: OverviewKpiCardProps | null) => {
      if (card) {
        cardsById.set(kpi.id, card);
      }
    };

    const totalPortfolioValue = findKpi(PORTFOLIO_NAV_KPI_IDS, PORTFOLIO_NAV_KPI_LABELS);
    if (totalPortfolioValue) {
      const metrics = computeHistoryMetrics(totalPortfolioValue);
      const valuationFootnote = buildValuationFootnote(totalPortfolioValue);
      registerCard(totalPortfolioValue, {
        title: 'Total portfolio value',
        value: formatCurrency(metrics.currentValue, {
          locale: tenantLocale,
          decimals: resolveCurrencyDecimals(metrics.currentValue),
          sign: 'never',
        }),
        valueTooltip:
          metrics.currentValue === null
            ? undefined
            : formatCurrency(metrics.currentValue, { locale: tenantLocale, decimals: 2, sign: 'never' }),
        icon: <DollarSign className="h-4 w-4 text-slate-400" aria-hidden="true" />,
        footnotes: [
          buildCurrencyFootnote('Δ 24h', metrics.deltaAbsolute, metrics.deltaPercent, tenantLocale),
          buildCurrencyFootnote(
            'vs 7-day avg',
            metrics.deltaVsAverageAbsolute,
            metrics.deltaVsAveragePercent,
            tenantLocale,
            { neutralTone: true },
          ),
          ...(valuationFootnote ? [valuationFootnote] : []),
        ],
        chip: buildValuationChip(totalPortfolioValue),
        sparklineData: metrics.sparklineValues,
        sparklineTrend: metrics.sparklineTrend,
        href: '/reports',
      });
    }

    const realizedPnl = findKpi(REALIZED_PNL_KPI_IDS, REALIZED_PNL_KPI_LABELS);
    if (realizedPnl) {
      const metrics = computeHistoryMetrics(realizedPnl);
      registerCard(realizedPnl, {
        title: 'Realized P&L',
        value: formatCurrency(metrics.currentValue, {
          locale: tenantLocale,
          decimals: resolveCurrencyDecimals(metrics.currentValue),
          sign: 'auto',
        }),
        valueTone: toneFromNumber(metrics.currentValue),
        valueTooltip:
          metrics.currentValue === null
            ? undefined
            : formatCurrency(metrics.currentValue, { locale: tenantLocale, decimals: 2, sign: 'auto' }),
        icon: <TrendingUp className="h-4 w-4 text-slate-400" aria-hidden="true" />,
        footnotes: [
          buildCurrencyFootnote('Δ 24h', metrics.deltaAbsolute, metrics.deltaPercent, tenantLocale),
          buildCurrencyFootnote(
            'vs 7-day avg',
            metrics.deltaVsAverageAbsolute,
            metrics.deltaVsAveragePercent,
            tenantLocale,
            { neutralTone: true },
          ),
        ],
        sparklineData: metrics.sparklineValues,
        sparklineTrend: metrics.sparklineTrend,
        href: '/reports',
      });
    }

    const sharpeRatio = byLabel.get('Sharpe Ratio');
    if (sharpeRatio) {
      const metrics = computeHistoryMetrics(sharpeRatio);
      const ratioValue = metrics.currentValue;
      registerCard(sharpeRatio, {
        title: 'Sharpe ratio',
        value:
          ratioValue === null
            ? '—'
            : thinSpace(formatNumber(ratioValue, { locale: tenantLocale, decimals: 2, sign: 'never' })),
        valueTooltip:
          ratioValue === null
            ? undefined
            : thinSpace(`Sharpe ${formatNumber(ratioValue, { locale: tenantLocale, decimals: 4, sign: 'never' })}`),
        icon: <Activity className="h-4 w-4 text-slate-400" aria-hidden="true" />,
        chip: getSharpeChip(ratioValue),
        footnotes: [
          buildNumberFootnote('Δ 24h', metrics.deltaAbsolute, 2, tenantLocale),
          buildPercentFootnote('vs 7-day avg', metrics.deltaVsAveragePercent, 2, tenantLocale, {
            neutralTone: true,
          }),
        ],
        sparklineData: metrics.sparklineValues,
        sparklineTrend: metrics.sparklineTrend,
        href: '/reports',
      });
    }

    const activeBots = byLabel.get('Active Bots');
    if (activeBots) {
      const metrics = computeHistoryMetrics(activeBots);
      registerCard(activeBots, {
        title: 'Active agents',
        value:
          metrics.currentValue === null
            ? '—'
            : formatNumber(metrics.currentValue, { locale: tenantLocale, decimals: 0, sign: 'never' }),
        valueTooltip:
          metrics.currentValue === null
            ? undefined
            : `${formatNumber(metrics.currentValue, { locale: tenantLocale, decimals: 0, sign: 'never' })} agents`,
        icon: <Bot className="h-4 w-4 text-slate-400" aria-hidden="true" />,
        footnotes: [
          buildNumberFootnote('Δ 24h', metrics.deltaAbsolute, 0, tenantLocale),
          buildPercentFootnote('vs 7-day avg', metrics.deltaVsAveragePercent, 2, tenantLocale, {
            neutralTone: true,
          }),
        ],
        sparklineData: metrics.sparklineValues,
        sparklineTrend: metrics.sparklineTrend,
        href: '/agents',
      });
    }

    kpis.forEach((kpi) => {
      if (cardsById.has(kpi.id)) {
        return;
      }
      registerCard(kpi, buildGenericCard(kpi));
    });

    const fallbackOrder = Array.from(cardsById.keys());
    const orderedBySelection = selectedKpiIds.filter((id) => cardsById.has(id));
    const extras = fallbackOrder.filter((id) => !orderedBySelection.includes(id));
    const finalOrder = [...orderedBySelection, ...extras].slice(0, MAX_KPI_SELECTION);

    return finalOrder
      .map((id) => cardsById.get(id) ?? null)
      .filter((card): card is OverviewKpiCardProps => Boolean(card));
  }, [kpis, selectedKpiIds, tenantLocale]);

  const equityChartData = useMemo(
    () =>
      metricsSeries.map((point) => ({
        timestamp: point.date,
        equity: point.equity ?? 0,
        pnl: point.pnl ?? 0,
      })),
    [metricsSeries],
  );

  const pnlChartData = useMemo(() => {
    let cumulative = 0;
    return metricsSeries.map((point) => {
      const pnlValue = point.pnl ?? 0;
      cumulative += pnlValue;
      return {
        timestamp: point.date,
        pnl: pnlValue,
        cumulativePnl: cumulative,
      };
    });
  }, [metricsSeries]);

  const volumeChartData = useMemo(
    () =>
      metricsSeries.map((point) => ({
        timestamp: point.date,
        volume: point.volume ?? 0,
        trades: point.trades ?? 0,
      })),
    [metricsSeries],
  );

  const volumeDataKeys = useMemo(() => {
    if (volumeView === 'trades') {
      return [
        {
          key: 'trades',
          label: 'Trades',
          color: '#A16207',
          valueType: 'number' as const,
        },
      ];
    }
    return [
      {
        key: 'volume',
        label: 'Volume (CHF)',
        color: '#1E40AF',
        valueType: 'currency' as const,
      },
    ];
  }, [tenantLocale, volumeView]);

  const volumeHeaderActions = useMemo(
    () => (
      <ToggleGroup
        type="single"
        value={volumeView}
        onValueChange={(value) => {
          if (value === 'chf' || value === 'trades') {
            setVolumeView(value);
          }
        }}
        className="h-8 rounded-full border border-slate-200 bg-white shadow-sm"
        aria-label="Toggle volume metric"
      >
        <ToggleGroupItem
          value="chf"
          className="px-3 py-1 text-xs font-semibold uppercase tracking-wide data-[state=on]:bg-slate-900 data-[state=on]:text-white"
        >
          CHF
        </ToggleGroupItem>
        <ToggleGroupItem
          value="trades"
          className="px-3 py-1 text-xs font-semibold uppercase tracking-wide data-[state=on]:bg-slate-900 data-[state=on]:text-white"
        >
          Trades
        </ToggleGroupItem>
      </ToggleGroup>
    ),
    [volumeView],
  );
  const totalOpenAlerts =
    alertsGrouping.open.recent.length + alertsGrouping.open.older.length;
  const totalResolvedAlerts =
    alertsGrouping.resolved.recent.length + alertsGrouping.resolved.older.length;
  const hasAlerts = totalOpenAlerts + totalResolvedAlerts > 0;

  const hasOverviewContent =
    kpis.length > 0 ||
    metricsSeries.length > 0 ||
    datasetAlerts.length > 0 ||
    datasetTrades.length > 0;

  const {
    exchangeSummaryCards,
    walletSummaryCards,
    connectionTotals,
  } = useMemo(() => {
    if (!connectionRecords || connectionRecords.length === 0) {
      return {
        exchangeSummaryCards: [] as ConnectionSummaryCard[],
        walletSummaryCards: [] as WalletSummaryCard[],
        connectionTotals: {
          exchanges: 0,
          wallets: 0,
          connectedExchanges: 0,
          connectedWallets: 0,
          totalBalance: 0,
        },
      };
    }

    const exchanges: ConnectionSummaryCard[] = [];
    const wallets: WalletSummaryCard[] = [];

    connectionRecords.forEach((connection, index) => {
      const fallbackKey =
        connection.connectionUuid ??
        (connection.id ? String(connection.id) : `connection-${index}`);
      const metadata = (connection.metadata ?? {}) as Record<string, unknown>;
      const status = normalizeConnectionStatus(connection.status);
      const type = normalizeConnectionType(connection);

      if (type === 'exchange') {
        exchanges.push({
          id: fallbackKey,
          name: resolveConnectionName(connection),
          status,
          activeBots: resolveConnectionActiveBots(metadata),
          balance: resolveConnectionBalance(metadata),
          currency: resolveConnectionCurrency(metadata),
          last24hVolume: resolveConnectionVolume(metadata),
          latencyMs: resolveConnectionLatency(metadata),
        });
        return;
      }

      if (type === 'wallet') {
        wallets.push({
          id: fallbackKey,
          name: resolveConnectionName(connection),
          status,
          network: resolveConnectionNetwork(connection, metadata),
          balance: resolveConnectionBalance(metadata),
          currency: resolveConnectionCurrency(metadata),
          latencyMs: resolveConnectionLatency(metadata),
        });
      }
    });

    const weight = (status: ConnectionSummaryCard['status']) => {
      switch (status) {
        case 'connected':
          return 0;
        case 'error':
          return 1;
        default:
          return 2;
      }
    };

    const sortedExchanges = [...exchanges].sort((a, b) => weight(a.status) - weight(b.status));
    const sortedWallets = [...wallets].sort((a, b) => weight(a.status) - weight(b.status));
    const totalBalance =
      sortedExchanges.reduce((sum, item) => sum + item.balance, 0) +
      sortedWallets.reduce((sum, item) => sum + item.balance, 0);

    return {
      exchangeSummaryCards: sortedExchanges.slice(0, 8),
      walletSummaryCards: sortedWallets.slice(0, 8),
      connectionTotals: {
        exchanges: sortedExchanges.length,
        wallets: sortedWallets.length,
        connectedExchanges: sortedExchanges.filter((item) => item.status === 'connected').length,
        connectedWallets: sortedWallets.filter((item) => item.status === 'connected').length,
        totalBalance,
      },
    };
  }, [connectionRecords]);

  const quickStats = useMemo<QuickStat[]>(() => {
    const stats: QuickStat[] = [];

    if (activitySummary.winRate !== undefined) {
      stats.push({
        label: 'Win Rate',
        value: activitySummary.winRate,
        format: 'percent',
        trend: activitySummary.winRate >= 50 ? 'up' : activitySummary.winRate < 40 ? 'down' : 'neutral',
      });
    }

    const portfolioKpi =
      kpis.find((k) => PORTFOLIO_NAV_KPI_IDS.includes(String(k.id ?? '').toLowerCase())) ??
      kpis.find((k) => PORTFOLIO_NAV_KPI_LABELS.includes(String(k.label ?? '').toLowerCase()));
    const maxDrawdownValue = portfolioKpi?.history
      ? (() => {
          const values = portfolioKpi.history
            .map((h) => parseNumeric(h.value))
            .filter((v): v is number => v !== null);
          if (values.length < 2) return null;
          let peak = values[0];
          let maxDrawdown = 0;
          values.forEach((v) => {
            if (v > peak) peak = v;
            const drawdown = ((peak - v) / peak) * 100;
            if (drawdown > maxDrawdown) maxDrawdown = drawdown;
          });
          return maxDrawdown;
        })()
      : null;

    if (maxDrawdownValue !== null) {
      stats.push({
        label: 'Max Drawdown',
        value: -maxDrawdownValue,
        format: 'percent',
        trend: maxDrawdownValue < 5 ? 'up' : maxDrawdownValue > 15 ? 'down' : 'neutral',
        invertTrendColor: true,
      });
    }

    const openPositions = datasetTrades.length > 0 ? Math.min(datasetTrades.length, 5) : 0;
    stats.push({
      label: 'Open Positions',
      value: openPositions,
      format: 'number',
      trend: 'neutral',
    });

    const totalBalance = connectionTotals.totalBalance;
    const riskExposure = totalBalance > 0 ? ((openPositions * 10000) / totalBalance) * 100 : 0;
    stats.push({
      label: 'Risk Exposure',
      value: Math.min(riskExposure, 100),
      format: 'percent',
      trend: riskExposure < 20 ? 'up' : riskExposure > 50 ? 'down' : 'neutral',
      invertTrendColor: true,
    });

    return stats;
  }, [activitySummary, connectionTotals.totalBalance, datasetTrades.length, kpis]);

  const enhancedExchangeConnections = useMemo<EnhancedExchangeConnection[]>(() => {
    const totalBalance = exchangeSummaryCards.reduce((sum, c) => sum + c.balance, 0);
    return exchangeSummaryCards.map((conn) => ({
      id: conn.id,
      name: conn.name,
      status: conn.status,
      balance: conn.balance,
      currency: conn.currency,
      activeBots: conn.activeBots,
      last24hVolume: conn.last24hVolume,
      latencyMs: conn.latencyMs,
      percentOfTotal: totalBalance > 0 ? (conn.balance / totalBalance) * 100 : 0,
    }));
  }, [exchangeSummaryCards]);

  const walletBalanceQueries = useQueries({
    queries: walletSummaryCards.map((wallet) => ({
      queryKey: ['overview-wallet-balances', wallet.id],
      enabled: scopeReady,
      staleTime: 1000 * 60 * 2,
      queryFn: async () => {
        const response = await connectionsAPI.listBalances(wallet.id);
        return Array.isArray(response) ? (response as WalletBalanceRecord[]) : [];
      },
    })),
  });

  const walletBalanceById = useMemo(() => {
    const summaries = new Map<string, WalletBalanceSummary>();

    walletSummaryCards.forEach((wallet, index) => {
      const rows = walletBalanceQueries[index]?.data;
      if (!Array.isArray(rows) || rows.length === 0) {
        return;
      }

      const latestByAsset = new Map<
        string,
        { quantity: number; valueUsd: number | null; valueChf: number | null; snapshotAt: string }
      >();

      rows.forEach((row) => {
        const symbolRaw = row.assetSymbol ?? row.asset_symbol;
        const assetSymbol =
          typeof symbolRaw === 'string' && symbolRaw.trim().length > 0
            ? symbolRaw.trim().toUpperCase()
            : 'ASSET';
        const snapshotRaw = row.snapshotAt ?? row.snapshot_at ?? '';
        const snapshotAt = typeof snapshotRaw === 'string' ? snapshotRaw : '';
        const quantity = parseConnectionNumber(row.quantity) ?? 0;
        const valueUsd = parseConnectionNumber(row.valueUsd ?? row.value_usd);
        const valueChf = parseConnectionNumber(row.valueChf ?? row.value_chf);
        const previous = latestByAsset.get(assetSymbol);
        if (!previous || Date.parse(snapshotAt || '') >= Date.parse(previous.snapshotAt || '')) {
          latestByAsset.set(assetSymbol, { quantity, valueUsd, valueChf, snapshotAt });
        }
      });

      if (latestByAsset.size === 0) {
        return;
      }

      let totalChf = 0;
      let totalUsd = 0;
      let hasChf = false;
      let hasUsd = false;
      let topAsset: { symbol: string; quantity: number } | null = null;

      const latestAssetEntries = Array.from(latestByAsset.entries());
      for (let index = 0; index < latestAssetEntries.length; index += 1) {
        const [symbol, entry] = latestAssetEntries[index];
        if (entry.valueChf !== null && Number.isFinite(entry.valueChf)) {
          totalChf += entry.valueChf;
          hasChf = true;
        }
        if (entry.valueUsd !== null && Number.isFinite(entry.valueUsd)) {
          totalUsd += entry.valueUsd;
          hasUsd = true;
        }
        if (!topAsset || entry.quantity > topAsset.quantity) {
          topAsset = { symbol, quantity: entry.quantity };
        }
      }

      if (hasChf) {
        summaries.set(wallet.id, {
          balance: totalChf,
          currency: 'CHF',
        });
        return;
      }

      if (hasUsd) {
        summaries.set(wallet.id, {
          balance: 0,
          currency: 'CHF',
          balanceLabel: formatCurrency(totalUsd, {
            locale: tenantLocale,
            currency: 'USD',
            decimals: totalUsd >= 10_000 ? 0 : 2,
            sign: 'never',
          }),
          balanceHint: 'USD valuation synced (CHF FX conversion pending)',
        });
        return;
      }

      if (latestByAsset.size === 1 && topAsset) {
        const decimals = Math.abs(topAsset.quantity) >= 100 ? 2 : 6;
        summaries.set(wallet.id, {
          balance: 0,
          currency: 'CHF',
          balanceLabel: `${formatNumber(topAsset.quantity, {
            locale: tenantLocale,
            decimals,
            sign: 'never',
          })} ${topAsset.symbol}`,
          balanceHint: 'Native balance synced (CHF valuation pending)',
        });
        return;
      }

      summaries.set(wallet.id, {
        balance: 0,
        currency: 'CHF',
        balanceLabel: `${latestByAsset.size} assets synced`,
        balanceHint: 'Asset balances synced (CHF valuation pending)',
      });
    });

    return summaries;
  }, [walletBalanceQueries, walletSummaryCards, tenantLocale]);

  const enhancedWalletConnections = useMemo<EnhancedWalletConnection[]>(
    () =>
      walletSummaryCards.map((wallet) => ({
        id: wallet.id,
        name: wallet.name,
        status: wallet.status,
        network: wallet.network,
        balance: walletBalanceById.get(wallet.id)?.balance ?? wallet.balance,
        currency: walletBalanceById.get(wallet.id)?.currency ?? wallet.currency,
        balanceLabel: walletBalanceById.get(wallet.id)?.balanceLabel,
        balanceHint: walletBalanceById.get(wallet.id)?.balanceHint,
        latencyMs: wallet.latencyMs,
      })),
    [walletBalanceById, walletSummaryCards],
  );

  const operationalKpiCards = useMemo<OverviewKpiCardProps[]>(() => {
    const runningBots = botsSummary?.running ?? 0;
    const pausedBots = botsSummary?.paused ?? 0;
    const stoppedBots = botsSummary?.stopped ?? 0;

    return [
      {
        title: 'Active agents',
        value: formatNumber(runningBots, { locale: tenantLocale, decimals: 0, sign: 'never' }),
        icon: <Bot className="h-4 w-4 text-slate-400" aria-hidden="true" />,
        footnotes: [
          {
            label: 'Paused',
            value: formatNumber(pausedBots, { locale: tenantLocale, decimals: 0, sign: 'never' }),
            tone: pausedBots > 0 ? 'neutral' : 'positive',
          },
          {
            label: 'Stopped',
            value: formatNumber(stoppedBots, { locale: tenantLocale, decimals: 0, sign: 'never' }),
            tone: stoppedBots > 0 ? 'negative' : 'neutral',
          },
        ],
        chip:
          runningBots > 0
            ? { label: `${formatNumber(runningBots, { locale: tenantLocale, decimals: 0, sign: 'never' })} live`, tone: 'positive' }
            : { label: 'No active agents', tone: 'neutral' },
        href: '/agents',
      },
    ];
  }, [botsSummary, tenantLocale]);

  const hasConnectionsSummary = exchangeSummaryCards.length > 0 || walletSummaryCards.length > 0;
  const isConnectionsLoading = connectionsLoading || connectionsFetching;
  const connectionsErrorMessage = connectionsError ? getErrorMessage(connectionsErrorData) : null;

  return (
    <>
      <StandardPageLayout
        title="Overview"
        headerContent={(
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  <h1 className="text-2xl font-semibold text-slate-900" data-testid="page-title">
                    Overview
                  </h1>
                  <span className="text-sm text-slate-500">— Trading performance &amp; system status</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {streamStatus === 'connected' ? (
                    <span
                      className="inline-flex items-center gap-2 text-slate-500"
                      role="status"
                      aria-live="polite"
                      aria-label="Live connection status: Connected"
                    >
                      <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden="true" />
                      <span>Last updated {formattedLastUpdated} UTC</span>
                    </span>
                  ) : (
                    <>
                      <LiveIndicator status={streamStatus} className="text-rose-600" />
                      <span className="text-slate-400">Last updated —</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3 sm:gap-4">
                <div className="flex items-center justify-end gap-3 sm:gap-4">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => setIsKillSwitchDialogOpen(true)}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 transition hover:border-rose-300 hover:text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60 focus-visible:ring-offset-2"
                          aria-label="Pause all agents"
                        >
                          <StopCircle className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Pause all agents</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {canManageKpis && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsKpiManagerOpen(true)}
                      className="h-9 rounded-full border-slate-200 px-3 text-slate-600 hover:bg-slate-50 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2"
                      data-testid="page-action-button"
                    >
                      <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />
                      Manage KPIs
                    </Button>
                  )}
                </div>
                <TimeRangeSelector
                  value={timeRange.value}
                  onChange={handleTimeRangeChange}
                  customRange={customRangeDates}
                  className="basis-full w-full min-w-[140px] sm:basis-auto sm:w-auto"
                  triggerClassName="h-9 w-full rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2 sm:w-[200px]"
                />
              </div>
            </div>
          </div>
        )}
        className={isMobile ? 'pb-24' : undefined}
      >
        <div className="space-y-4 lg:space-y-6">
          {!notificationBarDismissed && notificationBarItems.length > 0 && (
            <AlertNotificationBar
              items={notificationBarItems}
              onDismiss={() => setNotificationBarDismissed(true)}
            />
          )}

          <OverviewKpiGrid
            className={isRefetching ? 'pointer-events-none opacity-60 transition-opacity' : undefined}
            aria-busy={isRefetching}
          >
            {kpisLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <OverviewKpiSkeleton key={`kpi-skeleton-${index}`} />
              ))
            ) : kpiCards.length > 0 ? (
              <>
                {kpiCards.map((card) => (
                  <OverviewKpiCard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    valueTooltip={card.valueTooltip}
                    icon={card.icon}
                    footnotes={card.footnotes}
                    chip={card.chip}
                    sparklineData={card.sparklineData}
                    sparklineTrend={card.sparklineTrend}
                    href={card.href}
                  />
                ))}
                <OverviewKpiCard
                  title="Alerts"
                  value={alertCounts.total.toString()}
                  icon={<Bell className="h-4 w-4 text-slate-400" aria-hidden="true" />}
                  footnotes={[
                    {
                      label: 'Critical',
                      value: alertCounts.critical.toString(),
                      tone: alertCounts.critical > 0 ? 'negative' : 'neutral',
                    },
                    {
                      label: 'High',
                      value: alertCounts.high.toString(),
                      tone: alertCounts.high > 0 ? 'negative' : 'neutral',
                    },
                  ]}
                  chip={
                    alertCounts.critical > 0
                      ? { label: `${alertCounts.critical} critical`, tone: 'negative' }
                      : alertCounts.total === 0
                        ? { label: 'All clear', tone: 'positive' }
                        : undefined
                  }
                  href="/monitoring"
                />
                {operationalKpiCards.map((card) => (
                  <OverviewKpiCard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    valueTooltip={card.valueTooltip}
                    icon={card.icon}
                    footnotes={card.footnotes}
                    chip={card.chip}
                    sparklineData={card.sparklineData}
                    sparklineTrend={card.sparklineTrend}
                    href={card.href}
                  />
                ))}
              </>
            ) : (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-sm text-slate-500">
                  No KPI data available yet. Trading metrics will appear once activity is ingested.
                </CardContent>
              </Card>
            )}
          </OverviewKpiGrid>

        {hasOverviewContent ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,460px)]">
              <div className="space-y-4">
                <div className="w-full">
                  <EnhancedChart
                    title="Portfolio Equity Curve"
                    description="Track your portfolio performance over time"
                    data={equityChartData}
                    dataKeys={[
                      { key: 'equity', label: 'Portfolio Value', color: '#15803D', valueType: 'currency' },
                      { key: 'pnl', label: 'P&L', color: '#DC2626', valueType: 'currency' },
                    ]}
                    timeRange={timeRange.value}
                    onTimeRangeChange={handleTimeRangeChange}
                    customRange={customRangeDates}
                    locale={tenantLocale}
                    loading={overviewQuery.isLoading && equityChartData.length === 0}
                    refreshing={isRefetching}
                    options={{
                      enableLogScale: true,
                      enableDrawdown: true,
                      drawdownKey: 'equity',
                      heightVariant: 'standard',
                    }}
                    emptyMessage="No trades in selected range."
                  />
                </div>

                <TabbedChart
                  title="Performance Metrics"
                  description="Daily P&L and trading volume"
                  tabs={[
                    {
                      id: 'pnl',
                      label: 'Daily P&L',
                      badge: activitySummary.totalPnl > 0 ? `+${formatCurrency(activitySummary.totalPnl, { locale: tenantLocale, decimals: 0, sign: 'never' })}` : undefined,
                      content: (
                        <EnhancedChart
                          title="Daily P&L"
                          data={pnlChartData}
                          dataKeys={[{ key: 'pnl', label: 'Daily P&L', color: '#DC2626', valueType: 'currency' }]}
                          timeRange={timeRange.value}
                          onTimeRangeChange={handleTimeRangeChange}
                          customRange={customRangeDates}
                          locale={tenantLocale}
                          loading={overviewQuery.isLoading && pnlChartData.length === 0}
                          refreshing={isRefetching}
                          options={{
                            variant: 'sparkline',
                            showGrid: false,
                            yAxisLabel: 'CHF',
                            cumulativeKeys: ['pnl'],
                            showSeriesToggle: false,
                            heightVariant: 'compact',
                          }}
                          emptyMessage="No trades in selected range."
                        />
                      ),
                    },
                    {
                      id: 'volume',
                      label: 'Trading Volume',
                      badge: activitySummary.tradeCount > 0 ? activitySummary.tradeCount : undefined,
                      content: (
                        <EnhancedChart
                          title="Trading Volume"
                          data={volumeChartData}
                          dataKeys={volumeDataKeys}
                          timeRange={timeRange.value}
                          onTimeRangeChange={handleTimeRangeChange}
                          customRange={customRangeDates}
                          locale={tenantLocale}
                          loading={overviewQuery.isLoading && volumeChartData.length === 0}
                          refreshing={isRefetching}
                          options={{
                            yAxisLabel: volumeView === 'chf' ? 'CHF' : 'Trades',
                            showSeriesToggle: false,
                            heightVariant: 'compact',
                          }}
                          emptyMessage="No trades in selected range."
                        />
                      ),
                    },
                  ]}
                  headerActions={volumeHeaderActions}
                />
              </div>

              <div className="h-full">
                <LiveFeed
                  items={liveFeedItems}
                  summary={liveFeedSummary}
                  loading={activitiesLoading}
                  activeFilter={activityFilter}
                  onFilterChange={setActivityFilter}
                  locale={tenantLocale}
                  maxHeight="none"
                  className="h-full"
                />
              </div>
            </div>

            <div>
              {isConnectionsLoading ? (
                <Card className="border border-slate-200" aria-busy>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3">
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-9 w-20" />
                    </div>
                  </CardHeader>
                  <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div
                        key={`connection-skeleton-${index}`}
                        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                      >
                        <Skeleton className="mx-auto h-10 w-10 rounded-full" />
                        <Skeleton className="mx-auto mt-3 h-4 w-24" />
                        <Skeleton className="mx-auto mt-2 h-3 w-20" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : connectionsErrorMessage ? (
                <Card className="border border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Connections</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-amber-700">
                    <p>{connectionsErrorMessage}</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        void refetchConnections();
                      }}
                    >
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : hasConnectionsSummary ? (
                <EnhancedExchangeSummary
                  exchanges={enhancedExchangeConnections}
                  wallets={enhancedWalletConnections}
                  onAddExchange={handleManageConnections}
                  addButtonLabel="Manage"
                  locale={tenantLocale}
                  showPieChart={enhancedExchangeConnections.length > 1}
                />
              ) : (
                <Card className="border-2 border-dashed border-slate-200 bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-slate-700">Connections</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4 text-sm text-slate-500">
                    <p>
                      Link your exchange and wallet connections to monitor uptime, balances, and agent coverage directly from the
                      overview.
                    </p>
                    <div>
                      <Button type="button" size="sm" onClick={handleManageConnections}>
                        Go to Connections
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 space-y-4">
              <PlaceholderCard
                icon={<TrendingUp className="h-4 w-4 text-slate-400" />}
                title="Portfolio insights"
                description={
                  <>
                    <p>
                      Connect a live data source to unlock the equity curve, daily profit &amp; loss,
                      and trading volume dashboards.
                    </p>
                    <p className="mt-2">
                      Once trades start flowing in, this section will visualise performance across
                      the selected scope.
                    </p>
                  </>
                }
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PlaceholderCard
                  icon={<DollarSign className="h-4 w-4 text-slate-400" />}
                  title="Daily P&L"
                  description="Import historical executions or let agents trade live to analyse day-over-day profit and loss."
                />
                <PlaceholderCard
                  icon={<Activity className="h-4 w-4 text-slate-400" />}
                  title="Trading volume"
                  description="Trading volume and fill counts appear once connected venues start reporting activity."
                />
              </div>

              <PlaceholderCard
                icon={<Bot className="h-4 w-4 text-slate-400" />}
                title="Exchange connections"
                description="Link venues and deploy trading agents to view connectivity status, balances, and agent coverage."
              />
            </div>

            <div className="space-y-4">
              {canApprove && (
                <PlaceholderCard
                  icon={<FileCheck className="h-4 w-4 text-slate-400" />}
                  title="Pending approvals"
                  description="Operational approvals will display here once desks begin raising change or risk requests."
                />
              )}
              <PlaceholderCard
                icon={<Bell className="h-4 w-4 text-slate-400" />}
                title="System alerts"
                description="Platform monitoring alerts and incident notifications will surface in this feed."
              />
              <PlaceholderCard
                icon={<Activity className="h-4 w-4 text-slate-400" />}
                title="Live trading activity"
                description="The most recent fills and order events appear here as soon as live trading is enabled."
              />
              <PlaceholderCard
                icon={<Bot className="h-4 w-4 text-slate-400" />}
                title="Agent summary"
                description="Deploy trading agents to view running, paused, and halted states at a glance."
              />
            </div>
          </div>
        )}
      </div>

      {isMobile && (
        <div className="fixed bottom-6 right-5 flex flex-col items-end gap-4 sm:hidden">
          {canManageKpis ? (
            <Button
              type="button"
              size="icon"
              onClick={() => setIsKpiManagerOpen(true)}
              aria-label="Manage KPIs"
              className="h-12 w-12 rounded-full bg-rose-600 text-white shadow-lg transition hover:bg-rose-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-600"
            >
              <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
            </Button>
          ) : null}
          <Button
            asChild
            size="icon"
            className="h-12 w-12 rounded-full bg-red-600 text-white shadow-lg transition hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >
            <Link href="/agents" aria-label="Open Agent Control Room">
              <Plus className="h-5 w-5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      )}
    </StandardPageLayout>
    <AlertDialog open={isKillSwitchDialogOpen} onOpenChange={setIsKillSwitchDialogOpen}>
      <AlertDialogContent className="max-w-md border border-rose-200">
        <AlertDialogHeader>
          <AlertDialogTitle>Pause all agent activity?</AlertDialogTitle>
          <AlertDialogDescription>
            Engaging the kill switch will halt all agent activity for this tenant immediately. Manual reactivation is required to resume trading.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            Type <span className="font-semibold">{KILL_SWITCH_CONFIRMATION}</span> to confirm.
          </div>
          <div className="space-y-2">
            <Input
              value={killSwitchInput}
              onChange={(event) => setKillSwitchInput(event.target.value)}
              placeholder={KILL_SWITCH_CONFIRMATION}
              aria-label="Kill switch confirmation"
              autoFocus
            />
            {killSwitchError ? (
              <p className="text-xs text-rose-600">{killSwitchError}</p>
            ) : null}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={killSwitchLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-500"
            disabled={killSwitchInput.trim().toUpperCase() !== KILL_SWITCH_CONFIRMATION || killSwitchLoading}
            onClick={(event) => {
              event.preventDefault();
              void handleKillSwitchSubmit();
            }}
          >
            {killSwitchLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Pause all agents
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
      {canManageKpis && (
        <KPIManagerDialog
          open={isKpiManagerOpen}
          onOpenChange={setIsKpiManagerOpen}
          kpis={managerKpis}
          loading={managerLoading}
          onCreate={handleCreateKpi}
          onUpdate={handleUpdateKpi}
          onPublish={handlePublishKpi}
          onRetire={handleRetireKpi}
          onDelete={handleDeleteKpi}
          selectedKpiIds={selectedKpiIds}
          onSelectionChange={handleKpiSelectionChange}
          selectionLimit={MAX_KPI_SELECTION}
          selectionSaving={selectionSaving}
        />
      )}
    </>
  );
}
